import React, { useState } from 'react';
import styled from 'styled-components';
import { Button, Input, Text, Card, Badge, Table, Modal } from '@teals/ui-web';
import type { Column } from '@teals/ui-web';

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const SectionCard = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const FullWidthField = styled.div`
  grid-column: 1 / -1;
`;

const PaymentCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  border: 1px solid var(--border, #e2e8f0);
  border-radius: 10px;
  background: var(--bg-elevated, #f8fafc);
`;

const CardInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const CardBrand = styled.span`
  font-weight: 600;
  font-size: 0.9375rem;
  color: var(--text-primary, #0f172a);
`;

const CardExpiry = styled.span`
  font-size: 0.8125rem;
  color: var(--text-secondary, #64748b);
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  border-top: 2px solid var(--border, #e2e8f0);
  font-weight: 600;
  font-size: 0.9375rem;
  color: var(--text-primary, #0f172a);
`;

interface UsageRow {
  description: string;
  quantity: string;
  unitPrice: string;
  total: string;
}

interface InvoiceRow {
  date: string;
  invoiceNo: string;
  amount: string;
  status: 'paid' | 'pending' | 'failed';
}

const USAGE_DATA: UsageRow[] = [
  { description: 'API Calls', quantity: '12,450', unitPrice: '$0.002', total: '$24.90' },
  { description: 'Agent Sessions', quantity: '340', unitPrice: '$0.15', total: '$51.00' },
  { description: 'Storage (GB)', quantity: '8.2', unitPrice: '$0.10', total: '$0.82' },
];

const USAGE_TOTAL = '$76.72';

const INVOICE_DATA: InvoiceRow[] = [
  { date: '2026-05-01', invoiceNo: 'INV-2026-0042', amount: '$82.35', status: 'paid' },
  { date: '2026-06-01', invoiceNo: 'INV-2026-0051', amount: '$76.72', status: 'pending' },
  { date: '2026-04-01', invoiceNo: 'INV-2026-0033', amount: '$91.10', status: 'failed' },
];

const BADGE_VARIANT_MAP: Record<InvoiceRow['status'], 'success' | 'warning' | 'error'> = {
  paid: 'success',
  pending: 'warning',
  failed: 'error',
};

const usageColumns: Column<UsageRow>[] = [
  { key: 'description', header: 'Description' },
  { key: 'quantity', header: 'Quantity', align: 'right' },
  { key: 'unitPrice', header: 'Unit Price', align: 'right' },
  { key: 'total', header: 'Total', align: 'right' },
];

const invoiceColumns: Column<InvoiceRow>[] = [
  { key: 'date', header: 'Date' },
  { key: 'invoiceNo', header: 'Invoice #' },
  { key: 'amount', header: 'Amount', align: 'right' },
  {
    key: 'status',
    header: 'Status',
    render: (value: string) => (
      <Badge variant={BADGE_VARIANT_MAP[value as InvoiceRow['status']]}>{value}</Badge>
    ),
  },
  {
    key: 'download',
    header: '',
    align: 'right',
    render: () => (
      <Button variant="ghost" size="sm">
        Download
      </Button>
    ),
  },
];

const Payments: React.FC = () => {
  const [businessName, setBusinessName] = useState('');
  const [billingEmail, setBillingEmail] = useState('');
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [country, setCountry] = useState('');
  const [taxId, setTaxId] = useState('');
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <PageContainer>
      <Text variant="h2">Payments</Text>

      <SectionCard padding={24}>
        <Text variant="h4">Billing Profile</Text>
        <FieldGrid>
          <Input
            label="Business Name"
            value={businessName}
            onChange={(e) => setBusinessName(e.target.value)}
            placeholder="Acme Inc."
          />
          <Input
            label="Billing Email"
            type="email"
            value={billingEmail}
            onChange={(e) => setBillingEmail(e.target.value)}
            placeholder="billing@acme.com"
          />
          <Input
            label="Address Line 1"
            value={addressLine1}
            onChange={(e) => setAddressLine1(e.target.value)}
            placeholder="123 Main St"
          />
          <Input
            label="Address Line 2"
            value={addressLine2}
            onChange={(e) => setAddressLine2(e.target.value)}
            placeholder="Suite 400"
          />
          <Input
            label="City"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            placeholder="San Francisco"
          />
          <Input
            label="State / Province"
            value={state}
            onChange={(e) => setState(e.target.value)}
            placeholder="CA"
          />
          <Input
            label="Postal Code"
            value={postalCode}
            onChange={(e) => setPostalCode(e.target.value)}
            placeholder="94105"
          />
          <Input
            label="Country"
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            placeholder="United States"
          />
          <FullWidthField>
            <Input
              label="Tax ID"
              value={taxId}
              onChange={(e) => setTaxId(e.target.value)}
              placeholder="XX-XXXXXXX"
            />
          </FullWidthField>
        </FieldGrid>
        <Button variant="primary">Save Billing Info</Button>
      </SectionCard>

      <SectionCard padding={24}>
        <Text variant="h4">Payment Methods</Text>
        <PaymentCard>
          <CardInfo>
            <CardBrand>Visa •••• 4242</CardBrand>
            <CardExpiry>Expires 12/2027</CardExpiry>
          </CardInfo>
          <Badge variant="success">Default</Badge>
        </PaymentCard>
        <Button variant="outline" onClick={() => setModalOpen(true)}>
          Add Payment Method
        </Button>
      </SectionCard>

      <SectionCard padding={24}>
        <Text variant="h4">Current Usage</Text>
        <Table<UsageRow> columns={usageColumns} data={USAGE_DATA} rowKey="description" />
        <TotalRow>
          <span>Total</span>
          <span>{USAGE_TOTAL}</span>
        </TotalRow>
      </SectionCard>

      <SectionCard padding={24}>
        <Text variant="h4">Invoice History</Text>
        <Table<InvoiceRow> columns={invoiceColumns} data={INVOICE_DATA} rowKey="invoiceNo" />
      </SectionCard>

      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Add Payment Method"
        message="Payment method integration will be available when the billing provider is connected."
        acceptButtonText="OK"
      />
    </PageContainer>
  );
};

export default Payments;
